import React from "react";
import {
  Box,
  Button,
  ButtonBase,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { PageProps, graphql, navigate } from "gatsby";
import NavigationWrapper from "../components/layout/NavigationWrapper";
import SEO from "../components/Seo";
import COLORS from "../constants/colors";
import ReviewShowCase from "../components/sections/ReviewShowcase";
import Display from "../components/layout/Display";
import InlineFaq from "../components/faq/InlineFaq";
import StarRatings from "../assets/svgs/starratings.svg";
import useIsMobile from "../hooks/useIsMobile";
import PartnerBanks from "../components/PartnerBanks";
import { AppStoreRating, GooglePlayRating } from "../constants/text";
import QRButton from "../components/QRButton";
import AppShowcaseRow from "../components/AppShowcaseRow";
import ArrowRightLink from "../components/navigation/ArrowRightLink";
import ScrollingPhone from "../assets/svgs/phoneScrolling.svg";
import seoAppen from "../assets/images/seo-appen.png";
import { NavigationNames } from "../constants/navigationNames";

export const query = graphql`
  query AppPage {
    allSanityAppReview(limit: 4, sort: { date: DESC }) {
      edges {
        node {
          id
          content
          stars
          reviewer
          date(formatString: "DD-MM-YYYY")
          title
        }
      }
    }
    sanityFaqSection(pageName: { eq: "app" }, visible: { eq: true }) {
      sectionHeader
      questions {
        _rawAnswer
        question
        visible
      }
    }
  }
`;

const AppPage = ({ data }: PageProps<Queries.AppPageQuery>) => {
  const reviews = data.allSanityAppReview.edges.map((e) => ({
    content: e.node.content ?? "",
    title: e.node.title ?? "",
    starsNr: e.node.stars ?? 0,
    dateOfReview: e.node.date ?? Date(),
    reviewerName: e.node.reviewer ?? "",
  }));

  const isMobile = useIsMobile();

  const faq = data.sanityFaqSection ? data.sanityFaqSection : undefined;
  const faqQuestions = faq
    ? faq.questions
        ?.filter((q) => q?.visible)
        .map((q: any) => ({
          question: q?.question,
          answer: q?._rawAnswer,
        }))
    : [];

  return (
    <NavigationWrapper>
      <Stack component="section">
        <Container>
          <Stack
            flexDirection="row"
            pt={{ xs: "48px", md: "80px" }}
            pb={{ xs: "80px", md: "100px" }}
          >
            <Stack gap={{ xs: "48px", md: "24px" }}>
              <Stack
                gap={{ xs: "24px", md: "40px" }}
                alignItems="flex-start"
                maxWidth="500px"
              >
                <Stack>
                  <Typography
                    color={COLORS.WHITE[100]}
                    variant="h1Biro"
                    sx={{}}
                  >
                    Selvkjørende økonomi
                  </Typography>
                  <Stack gap={{ xs: "24px", md: "32px" }}>
                    <Typography variant="h1" color={COLORS.BRIGHT_BLUE[60]}>
                      Horde-appen
                    </Typography>
                    <Typography variant="lead">
                      Få kontroll på gjelden, og hvordan den utvikler seg. Koble
                      til kontoer du bruker, uavhengig av hvilke banker du har
                      dem i. Horde gir deg full kontroll og oversikt. Du er
                      sjefen!
                    </Typography>
                  </Stack>
                </Stack>

                <QRButton label="Last ned appen" color="lightOnDark" />
              </Stack>
              <Stack
                flexDirection={{ xs: "column", md: "row" }}
                gap={{ xs: "32px", md: "48px" }}
              >
                <ButtonBase
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    borderRadius: "20px",
                    p: "12px",
                    gap: "16px",
                  }}
                  target="_blank"
                  href="https://apps.apple.com/no/app/horde-sjef-i-egen-%C3%B8konomi/id1471960734?platform=iphone"
                >
                  <Stack>
                    <Typography component="u" fontWeight="700">
                      App Store
                    </Typography>
                    <Stack flexDirection="row" gap="4px">
                      <Typography component="u">
                        {AppStoreRating.split(" · ")[0]}
                      </Typography>
                      <Typography> · </Typography>
                      <Typography component="u">
                        {AppStoreRating.split(" · ")[1]}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack>
                    <StarRatings />
                  </Stack>
                </ButtonBase>
                <ButtonBase
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    borderRadius: "20px",
                    p: "12px",
                    gap: "16px",
                  }}
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=com.horde&hl=no"
                >
                  <Stack>
                    <Typography component="u" fontWeight="700">
                      Google Play
                    </Typography>
                    <Stack flexDirection="row" gap="4px">
                      <Typography component="u">
                        {GooglePlayRating.split(" · ")[0]}
                      </Typography>
                      <Typography> · </Typography>
                      <Typography component="u">
                        {GooglePlayRating.split(" · ")[1]}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack>
                    <StarRatings />
                  </Stack>
                </ButtonBase>
              </Stack>
            </Stack>
            <Stack
              alignItems="flex-end"
              flex={1}
              display={{ xs: "none", md: "flex" }}
            >
              <ScrollingPhone height="400px" />
            </Stack>
          </Stack>
        </Container>
      </Stack>

      <AppShowcaseRow
        imgName="min-gjeld.webp"
        imgAlt="Oversikt over usikret gjeld"
        content={
          <>
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Følg med på gjelden
            </Typography>
            <ul className="ul-space">
              <li>
                <Typography color="primary">
                  Se gjelds- og rentehistorikk og få et oppdatert bilde av
                  hvordan du ligger an med gjelden akkurat nå.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Få varsel når et kredittkort blir rentebærende. Ta grep og
                  nedbetal kreditten direkte i appen.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Sammenlign renten din med gjennomsnittet av Horde-brukere. Søk
                  bedre lånebetingelser via appen.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Si opp kredittkort du ikke har behov for.
                </Typography>
              </li>
            </ul>
          </>
        }
      />

      <AppShowcaseRow
        reverse
        imgName="boligverdi_miljø.png"
        imgAlt="Følg med på boligverdien din"
        content={
          <>
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Følg med på boligverdien din
            </Typography>
            <ul className="ul-space">
              <li>
                <Typography color="primary">
                  Se estimert verdi på bolig du eier.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Legg til alle boliger du eier, fritidsboliger kommer snart.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Følg utviklingen over tid, grafen oppdateres ukentlig.
                </Typography>
              </li>
            </ul>
          </>
        }
      />
      <AppShowcaseRow
        imgName="bedre-lan.webp"
        imgAlt="Søk bedre lån i appen"
        gap="12px"
        content={
          <>
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Søk bedre lån
            </Typography>
            <Typography color="primary">
              Vi samarbeider med mange banker, og sender søknaden din til alle.
              Det er selvfølgelig helt uforpliktende å søke.
            </Typography>
            <ul className="ul-space">
              <li>
                <Typography color="primary">
                  Nedbetal lånet, få poeng.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Sjekk om du kan få bedre lån.
                </Typography>
              </li>
            </ul>
            <Button
              color="dark"
              onClick={() => {
                navigate(NavigationNames.SOKNAD.START_SOKNAD);
              }}
            >
              Start søknad
            </Button>
            <Stack>
              <Typography color="primary" variant="loanInterestExampleTitle">
                Renteeksempel refinansering:
              </Typography>
              <Typography
                color="primary"
                variant="loanInterestExampleDescription"
              >
                147 000 kr over 7 år, effektiv rente 13,59 %, kostnad 76 101 kr,
                totalt 223 101 kr
              </Typography>
            </Stack>
          </>
        }
      />
      <AppShowcaseRow
        reverse
        imgName="kredittscore.png"
        imgAlt="Følg med på egen kredittscore"
        content={
          <>
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Kredittsjekk deg selv
            </Typography>
            <Typography color="primary" sx={{ maxWidth: "290px" }}>
              Vi henter data fra kredittopplysningsbyrået Bislab.
            </Typography>
            <ul className="ul-space">
              <li>
                <Typography color="primary">
                  Sjekk kredittscore i appen.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Sjekk om du har betalingsanmerkninger, og bli varslet om du
                  får nye
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Jo høyere score, jo bedre betingelser kan du få hos bankene.
                </Typography>
              </li>
            </ul>
            <ArrowRightLink
              href="https://horde.no/artikler/ny-tjeneste-vi-lanserer-kredittscore"
              text="Les om tjenesten Kredittscore"
            />
          </>
        }
      />
      <AppShowcaseRow
        imgName="mine-penger.webp"
        imgAlt="Samle alle kontoene dine ett sted"
        content={
          <>
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Samle alle kontoene dine ett sted
            </Typography>
            <ul className="ul-space">
              <li>
                <Typography color="primary">
                  Få full oversikt, helt uavhengig av hvilke banker du har
                  kontoene dine i.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Overfør penger på tvers av banker.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Følg med på forbruk og kontobevegelser.
                </Typography>
              </li>
            </ul>
          </>
        }
      />

      <AppShowcaseRow
        imgName="rewards.webp"
        reverse
        imgAlt="Tjen Horde rewards poeng i appen"
        content={
          <>
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Bli belønnet med horde-poeng
            </Typography>
            <Typography color="primary">
              Vi heier på gode økonomiske vaner:
            </Typography>
            <ul className="ul-space">
              <li>
                <Typography color="primary">Betal ned gjeld.</Typography>
              </li>
              <li>
                <Typography color="primary">
                  Svar riktig på vår ukentlige pengequiz.
                </Typography>
              </li>
              <li>
                <Typography color="primary">
                  Betal ned kredittkortgjeld.
                </Typography>
              </li>
              <li>
                <Typography color="primary">Refinansier dyre lån.</Typography>
              </li>
              <li>
                <Typography color="primary">Verv venner og kjente.</Typography>
              </li>
              <li>
                <Typography color="primary">
                  Du kan maks tjene 100 000 poeng.
                </Typography>
              </li>
            </ul>
            <ArrowRightLink href="/rewards" text="Mer om Horde Rewards" />
          </>
        }
      />

      <Stack
        component="section"
        bgcolor={COLORS.DARK_BLUE[100]}
        py={{ xs: "40px", md: "110px" }}
      >
        <Container>
          <Stack
            gap={{ xs: "16px", md: "160px" }}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Stack flex={{ xs: "1", md: "2" }}>
              <Typography color={COLORS.BRIGHT_BLUE[60]} variant="appOBTitle">
                Open banking handler om deg.
              </Typography>
            </Stack>
            <Stack flex={{ xs: "1", md: "5" }}>
              <Typography>
                Enkelt sagt handler open banking om at vi trygt og sømløst kan
                dele det som har med økonomien din å gjøre, på tvers av apper og
                banker. Med Horde får du alt tilgjengelig ett sted, på et språk
                du forstår.
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Stack>

      <Box
        component="section"
        bgcolor={COLORS.BRIGHT_BLUE[40]}
        pt={isMobile ? 0 : "110px"}
      >
        <Box
          bgcolor={{ xs: COLORS.BRIGHT_BLUE[60], md: COLORS.BRIGHT_BLUE[40] }}
        >
          <ReviewShowCase reviews={reviews} bgcolor={COLORS.BRIGHT_BLUE[60]} />
        </Box>
      </Box>
      <Stack component="section">
        <Box
          bgcolor={COLORS.BRIGHT_BLUE[40]}
          display="flex"
          alignContent="center"
          justifyContent="center"
          py={{ md: "80px", xs: "25px" }}
        >
          <Container>
            <Display desktop>
              <Box
                bgcolor={COLORS.BRIGHT_BLUE[20]}
                py="70px"
                px="180px"
                borderRadius={isMobile ? "0" : "20px"}
              >
                {faq && (
                  <InlineFaq
                    contactPrompt
                    headerText={faq.sectionHeader ?? ""}
                    items={faqQuestions}
                  />
                )}
              </Box>
            </Display>
            <Display mobile>
              {faq && (
                <InlineFaq
                  contactPrompt
                  headerText={faq.sectionHeader ?? ""}
                  items={faqQuestions}
                />
              )}
            </Display>
          </Container>
        </Box>
      </Stack>
      <Stack
        component="section"
        bgcolor={COLORS.BRIGHT_BLUE[40]}
        py={{ xs: "24px", md: "48px" }}
      >
        <Container disableGutters={isMobile}>
          <Stack gap="48px" alignItems="center">
            <Typography variant="h2" color={COLORS.BLUE[100]}>
              Våre partnere
            </Typography>
            <PartnerBanks />
          </Stack>
        </Container>
      </Stack>
    </NavigationWrapper>
  );
};

export default AppPage;

export function Head() {
  return (
    <SEO
      title="Appen - Horde"
      description="Få kontroll på gjelden med Horde-appen. Koble til kontoer fra alle banker og få full oversikt. Horde gir deg kontrollen - du er sjefen"
      image={seoAppen}
    />
  );
}
